import { useEffect, useState } from "preact/hooks"
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts"
import { ChartMetric } from "../types/distributor_sales"
import {
  DistributorSalesPieLegend,
  DistributorSalesPieSliceTooltip,
  PieSlicePercentLabel,
  cellFillOpacity,
  pieOuterRadius,
  sliceFill,
} from "./distributor_sales_pie_shared"

export interface DistributorSalesInteractivePieSlice {
  name: string
  value: number
}

export type DistributorSalesInteractivePieLayout = "modal" | "card"

interface DistributorSalesInteractivePieProps {
  slices: DistributorSalesInteractivePieSlice[]
  metric: ChartMetric
  layout: DistributorSalesInteractivePieLayout
  /** Appended to the chart+legend row (e.g. `flex-1 min-h-0` for modal, `min-h-[280px]` for card). */
  rowClassName?: string
  legendListMaxHeightClass?: string
  legendClassName?: string
}

/**
 * Recharts pie + shared legend/tooltip/hover behavior for hierarchy breakdown modal (table) and SKU summary card.
 */
export function DistributorSalesInteractivePie({
  slices,
  metric,
  layout,
  rowClassName = "",
  legendListMaxHeightClass,
  legendClassName = "",
}: DistributorSalesInteractivePieProps) {
  const [hoverHighlight, setHoverHighlight] = useState<number | null>(null)
  const [lockedIndex, setLockedIndex] = useState<number | null>(null)

  const legendEmphasisIndex = hoverHighlight !== null ? hoverHighlight : lockedIndex
  const pieEmphasisIndex = hoverHighlight

  useEffect(() => {
    setHoverHighlight(null)
    setLockedIndex(null)
  }, [metric])

  const total = slices.reduce((s: number, p: DistributorSalesInteractivePieSlice) => s + p.value, 0)

  function toggleLockAtIndex(idx: number) {
    setLockedIndex((prev: number | null) => (prev === idx ? null : idx))
  }

  function clearPieHoverIfLeftChart(e: PointerEvent) {
    const el = e.currentTarget as HTMLElement
    const next = e.relatedTarget as Node | null
    if (next && el.contains(next)) return
    setHoverHighlight(null)
  }

  const defaultLegendMax =
    layout === "card" ? "max-h-[min(40vh,280px)]" : "max-h-[min(52vh,28rem)]"
  const chartShellClass =
    layout === "card"
      ? "relative h-[260px] w-full sm:w-[280px] shrink-0 mx-auto sm:mx-0"
      : "relative h-[260px] w-full sm:w-[min(100%,280px)] sm:shrink-0 mx-auto sm:mx-0"

  return (
    <div class={`flex flex-col sm:flex-row gap-4 items-stretch ${rowClassName}`}>
      <div class={chartShellClass} onPointerLeave={clearPieHoverIfLeftChart}>
        <DistributorSalesPieSliceTooltip
          slice={hoverHighlight !== null ? slices[hoverHighlight] : null}
          metric={metric}
          total={total}
        />
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={slices}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={pieOuterRadius(slices.length, layout)}
              labelLine={false}
              label={PieSlicePercentLabel}
              isAnimationActive={false}
              animationDuration={0}
              onMouseEnter={(_: unknown, index: number) => { setHoverHighlight(index) }}
              onMouseLeave={() => { setHoverHighlight(null) }}
              onClick={(_: unknown, index: number) => { toggleLockAtIndex(index) }}
            >
              {slices.map((entry: DistributorSalesInteractivePieSlice, idx: number) => (
                <Cell
                  key={`${entry.name}-${idx}`}
                  fill={sliceFill(entry.name, idx)}
                  fillOpacity={cellFillOpacity(idx, pieEmphasisIndex)}
                  stroke={pieEmphasisIndex === idx ? "#0f172a" : "transparent"}
                  strokeWidth={pieEmphasisIndex === idx ? 2 : 0}
                />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>
      <DistributorSalesPieLegend
        slices={slices}
        metric={metric}
        total={total}
        emphasisIndex={legendEmphasisIndex}
        onRowPointerEnter={(idx: number) => { setHoverHighlight(idx) }}
        onRowPointerLeave={() => { setHoverHighlight(null) }}
        onRowClick={toggleLockAtIndex}
        listMaxHeightClass={legendListMaxHeightClass ?? defaultLegendMax}
        className={legendClassName}
      />
    </div>
  )
}
